// The waiting accounts while one is open (33-accounts.md, "Counts and notifications of waiting
// accounts", "A room of another account", "Signing out"): their names from their own servers, a
// count check every 30 s that notifies when it grows and feeds the counts in the menu and on the
// rail (a candidate the owner kept), removing them.
import type { MatrixClient } from 'matrix-js-sdk';
import { ask, serverOf, type Who } from '../core/session';
import { setting } from '../core/settings';
import { keep as keepMail } from '../core/mail';
import { t } from '../i18n';
import { atMost, capped, databasesLeft, databasesOf } from '../core/device';
import { dropIndex } from '../saved/searchIndex';
import { silenced } from '../status/status';
import { signOutOfCompany } from '../session/admin';
import { config } from '../core/config';
import { forgetDevice, nameDevice } from '../id/sessions';
import { mailAccess, mailToken } from '../core/mail';
import { companyEntry } from '../signin/company';
import { defaultAccount, deleteDatabasesOf, everyAccount, keepWaiting, setDefault, switchTo, waiting, type Account } from './store';
import { circleLook, keepCircleLook } from './switching';

const FILTER = JSON.stringify({
  presence: { not_types: ['*'] },
  account_data: { not_types: ['*'] },
  room: { state: { not_types: ['*'] }, timeline: { limit: 1 }, ephemeral: { not_types: ['*'] }, account_data: { not_types: ['*'] } },
});

interface Known {
  name?: string;
  picture?: string;
  since?: string;
  /** Per room: notifying unread messages, and those for the person. */
  rooms: Map<string, [number, number]>;
  invites: Set<string>;
  total?: number;
  forYou?: number;
}
const known = new Map<string, Known>();
const listeners = new Set<() => void>();
export const onAccounts = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));
const changed = () => listeners.forEach((l) => l());

export const nameOf = (a: Account) => known.get(a.user_id)?.name ?? a.user_id.slice(1).split(':')[0];

export interface Count {
  n: number;
  red: boolean;
}
/** A waiting account's count (33): the "for you" count when there is one, else all news and invitations. */
export function countOf(a: Account): Count | undefined {
  const k = known.get(a.user_id);
  return a.signed_out || !k?.total ? undefined : { n: k.forYou || k.total, red: !!k.forYou };
}
/** All the waiting accounts' counts together: the header card's badge and the rail's dot. */
export const othersCount = (): Count | undefined =>
  waiting().map(countOf).reduce<Count | undefined>((sum, c) => (c ? { n: (sum?.n ?? 0) + c.n, red: !!sum?.red || c.red } : sum), undefined);


/** A picture through the account's own server and token, as a local address; tried once more after about 2 s (02), none when that fails. */
async function media(a: Who, url: unknown, size: number, again = true): Promise<string | undefined> {
  const mxc = typeof url === 'string' && url.match(/^mxc:\/\/([^/]+)\/(.+)$/);
  if (!mxc) return undefined;
  const picture = await ask(a, `/v1/media/thumbnail/${mxc[1]}/${mxc[2]}?width=${size}&height=${size}&method=crop`).catch(() => undefined);
  if (picture?.ok) return URL.createObjectURL(await picture.blob());
  return again ? new Promise((r) => setTimeout(() => r(media(a, url, size, false)), 2000)) : undefined;
}

/** The display name and a 64 px picture from the account's own server; a picture that fails leaves the initials. */
async function readProfile(a: Account, k: Known) {
  const answer = await ask(a, `/v3/profile/${encodeURIComponent(a.user_id)}`);
  if (!answer.ok) return;
  const { displayname, avatar_url } = await answer.json();
  k.name = displayname || undefined;
  k.picture = await media(a, avatar_url, 64);
  changed();
}

/**
 * Each account's circle (33, "Circle profile"): its name and logo from its circle's admin service
 * (`GET /circle`, with the account's own token), kept for the session; without one, the first part
 * of its chat server's name with a capital, and the logo its initials.
 */
const circles = new Map<string, { name?: string | null; picture?: string; mxc?: string | null; banner?: string | null }>();
const reading = new Set<string>();
export const circleName = (userId: string) => circles.get(userId)?.name || circleLook(userId)?.name || serverOf(userId).replace(/^./, (c) => c.toLocaleUpperCase()).split('.')[0];
export const circlePicture = (userId: string) => circles.get(userId)?.picture;
export const circleMxc = (userId: string) => circles.get(userId)?.mxc ?? null;
/** The circle's own banner (an mxc URI; absent from an older service, v1.58.32). */
export const circleBanner = (userId: string) => circles.get(userId)?.banner ?? null;
/** The service sends `banner` at all (even as null): only then may admins set one (27, v1.58.32). */
export const circleHasBanner = (userId: string) => circles.get(userId)?.banner !== undefined;
/** Its profile is being asked for and this device knows nothing of it yet: skeleton parts in its row (36, v1.58.25). */
export const circleLoading = (userId: string) => reading.has(userId) && !circles.has(userId) && !circleLook(userId);
/** Two accounts on this device in one circle show their addresses to tell them apart (33). */
export const sameCircle = (userId: string) => [currentUser, ...waiting().map((w) => w.user_id)].filter((u) => serverOf(u) === serverOf(userId)).length > 1;
let currentUser = '';

export async function readCircle(a: Who) {
  const entry = companyEntry(config(), a.base_url) ?? companyEntry(config(), serverOf(a.user_id));
  if (!entry?.admin) return;
  const mail = !!mailAccess(a.user_id);
  const answer = await fetch(`${new URL(entry.admin, location.origin).href.replace(/\/+$/, '')}/circle`, {
    credentials: 'omit',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${mail ? await mailToken(a.user_id, false) : a.access_token}`, ...(mail ? {} : { 'X-Desyr-Chat-Server': serverOf(a.user_id) }) },
  });
  if (!answer.ok) return;
  const { name, picture, banner } = await answer.json();
  circles.set(a.user_id, { name, mxc: picture, picture: await media(a, picture, 80), banner });
  if (name) keepCircleLook(a.user_id, name);
  changed();
}
/** The open account and the waiting ones: their circles asked again (the accounts view opening). */
export const readCircles = (client: MatrixClient) =>
  everyAccount(client).forEach((a) => void (reading.add(a.user_id), readCircle(a).catch(() => {}).finally(() => (reading.delete(a.user_id), changed()))));

/** One count check: a 401 marks the account signed out; any other failure changes nothing. */
async function check(client: MatrixClient, a: Account) {
  const k = known.get(a.user_id)!;
  const query = new URLSearchParams({ timeout: '0', set_presence: 'offline', filter: FILTER, ...(k.since ? { since: k.since } : {}) });
  const answer = await ask(a, `/v3/sync?${query}`);
  if (answer.status === 401) {
    // A session from desyr ID's sessions call is renewed first (36, Behaviour 6.4); the next round uses it.
    if (a.expires_at !== undefined && (await import('../id/sessions').then((m) => m.renewRefused(config(), a.user_id, a.access_token))) !== 'ended') return;
    // With desyr ID, a circle the list no longer holds goes quietly instead (39, Behaviour 2.2).
    if (await import('../id/Leave').then((m) => m.removedFrom(a.user_id))) return;
    keepWaiting(waiting().map((w) => (w.user_id === a.user_id ? { ...w, signed_out: true } : w)));
    return changed();
  }
  if (!answer.ok) return;
  if (a.expires_at !== undefined) void nameDevice(a); // a device from desyr ID's sessions call (04, Device names)
  const body = await answer.json();
  for (const [id, room] of Object.entries<{ unread_notifications?: { notification_count?: number; highlight_count?: number } }>(body.rooms?.join ?? {})) {
    k.rooms.set(id, [room.unread_notifications?.notification_count ?? 0, room.unread_notifications?.highlight_count ?? 0]);
    k.invites.delete(id);
  }
  for (const id of Object.keys(body.rooms?.invite ?? {})) k.invites.add(id);
  for (const id of Object.keys(body.rooms?.leave ?? {})) (k.rooms.delete(id), k.invites.delete(id));
  k.since = body.next_batch;
  const [before, beforeForYou] = [k.total, k.forYou];
  k.total = [...k.rooms.values()].reduce((sum, [n]) => sum + n, 0) + k.invites.size;
  k.forYou = [...k.rooms.values()].reduce((sum, [, h]) => sum + h, 0);
  if (k.total !== before || k.forYou !== beforeForYou) changed();
  // Never on the first check; not while notifications are off, silenced or not allowed.
  if (before === undefined || k.total <= before || !setting('desktop-notifications') || silenced(client)) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const registration = await navigator.serviceWorker?.getRegistration();
  await registration?.showNotification(`${nameOf(a)} · ${circleName(a.user_id)}`, {
    body: t('accounts.newMessages', { n: k.total - before }),
    tag: `desyr-account-${a.user_id}`,
    icon: k.picture,
    data: { url: '/', account: a.user_id },
  });
}

/** Checks the waiting accounts now and every 30 s (never offline); a notification's click switches. */
export function watchWaiting(client: MatrixClient): () => void {
  currentUser = client.getUserId()!;
  readCircles(client);
  const round = () => {
    if (navigator.onLine === false) return;
    for (const a of waiting().filter((w) => !w.signed_out)) {
      if (!known.has(a.user_id)) {
        known.set(a.user_id, { rooms: new Map(), invites: new Set() });
        void readProfile(a, known.get(a.user_id)!).catch(() => {});
      }
      void check(client, a).catch(() => {});
    }
  };
  round();
  const timer = setInterval(round, 30_000);
  const onWorker = (e: MessageEvent) => {
    const a = e.data?.type === 'desyr:open' && waiting().find((w) => w.user_id === e.data.account);
    if (a) switchTo(a.user_id, e.data.url, circleName(a.user_id));
  };
  navigator.serviceWorker?.addEventListener('message', onWorker);
  return () => {
    clearInterval(timer);
    navigator.serviceWorker?.removeEventListener('message', onWorker);
  };
}

/** A waiting account goes: its company's sign-in session ended, its mail access forgotten, its server told, its databases and index deleted. */
export async function removeWaiting(a: Account, by?: number): Promise<void> {
  // Within a sign-out (`by`, 03 Deadlines, v1.58.74): its requests capped; the databases go at the next start.
  const cap = <T>(work: Promise<T>) => (by ? capped(work, by) : work.catch(() => undefined));
  await cap(signOutOfCompany(a.base_url, a.user_id, a.access_token));
  keepMail(a.user_id, undefined);
  await cap(ask(a, '/v3/logout', { method: 'POST', body: '{}' }));
  if (by) databasesLeft(databasesOf(a.db, a.user_id));
  else {
    await deleteDatabasesOf(a.db).catch(() => {});
    await atMost(dropIndex(a.user_id, true));
  }
  keepWaiting(waiting().filter((w) => w.user_id !== a.user_id));
  forgetDevice(); // a sign-out here: the next new circle gets a new device (36, Behaviour 6.2)
  if (defaultAccount() === a.user_id) setDefault(null);
  known.delete(a.user_id);
  changed();
}

/** The first waiting account that is a member of this room. */
export async function otherAccountFor(roomId: string): Promise<Account | undefined> {
  for (const a of waiting().filter((w) => !w.signed_out)) {
    const answer = await ask(a, `/v3/rooms/${encodeURIComponent(roomId)}/joined_members`).catch(() => undefined);
    if (answer?.ok && a.user_id in ((await answer.json()).joined ?? {})) return a;
  }
  return undefined;
}
